import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { i18n } from '@wms/shared';
import { api } from '../lib/api.js';
import { formatDate } from '../lib/format.js';
import { usePaged } from '../lib/paged.js';
import { SearchSelect } from './SearchSelect.js';

const { t } = i18n;

const MOVEMENT_TYPES = [
  'INBOUND_PALLET',
  'INBOUND_DIRECT',
  'CONSUMPTION',
  'ASSEMBLY_IN',
  'DELIVERY_OUT',
  'MANUAL_IN',
  'MANUAL_OUT',
  'ADJUSTMENT',
] as const;
type MovementType = (typeof MOVEMENT_TYPES)[number];

interface Movement {
  id: string;
  skuId: string;
  qty: string;
  type: MovementType;
  timestamp: string;
  reason: string | null;
  comment: string | null;
  sourcePalletId: string | null;
  sourcePallet: { id: string; code: string } | null;
  sourceOrderId: string | null;
  sourceOrder: { id: string; code: string } | null;
  actor: { displayName: string; email: string } | null;
  sku: { code: string; nameLt: string };
}

interface SkuOpt {
  id: string;
  code: string;
  type: 'PRODUCT' | 'COMPONENT';
  nameLt: string;
  photoUrl: string | null;
  availableQty: number;
  maxProducibleQty: number | null;
}

const TYPE_CLASS: Record<MovementType, string> = {
  INBOUND_PALLET:
    'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-100',
  INBOUND_DIRECT:
    'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-100',
  CONSUMPTION:
    'bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-900/40 dark:text-violet-100',
  ASSEMBLY_IN: 'bg-teal-100 text-teal-900 border-teal-300 dark:bg-teal-900/40 dark:text-teal-100',
  DELIVERY_OUT: 'bg-rose-100 text-rose-900 border-rose-300 dark:bg-rose-900/40 dark:text-rose-100',
  MANUAL_IN: 'bg-blue-100 text-blue-900 border-blue-300 dark:bg-blue-900/40 dark:text-blue-100',
  MANUAL_OUT:
    'bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-900/40 dark:text-orange-100',
  ADJUSTMENT: 'bg-muted text-muted-foreground border-border',
};

export function StockMovementsTable({
  partnerId,
  reloadKey,
  showExport = true,
  pageSize = 20,
}: {
  partnerId: string;
  reloadKey: number;
  showExport?: boolean;
  pageSize?: number;
}) {
  const [rows, setRows] = useState<Movement[] | null>(null);
  const [skus, setSkus] = useState<SkuOpt[]>([]);
  const [skuFilter, setSkuFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState<MovementType | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const paged = usePaged(rows ?? [], pageSize);

  useEffect(() => {
    api<SkuOpt[]>(`/partners/${partnerId}/skus`)
      .then(setSkus)
      .catch(() => setSkus([]));
  }, [partnerId]);

  useEffect(() => {
    setRows(null);
    const params = new URLSearchParams();
    if (skuFilter) params.set('skuId', skuFilter);
    if (typeFilter) params.set('type', typeFilter);
    if (from) params.set('from', from);
    if (to) params.set('to', to);
    const qs = params.toString();
    api<Movement[]>(`/partners/${partnerId}/stock/movements${qs ? `?${qs}` : ''}`)
      .then(setRows)
      .catch(() => setRows([]));
  }, [partnerId, reloadKey, skuFilter, typeFilter, from, to]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          {t('stock.movements.title')}
        </h3>
        {showExport ? (
          <a
            href={`/api/reports/movements.csv?partnerId=${partnerId}${from ? `&fromDate=${from}` : ''}${to ? `&toDate=${to}` : ''}`}
            download
            className="rounded border border-border px-2 py-1 text-xs hover:bg-muted"
          >
            {t('reports.download_csv')}
          </a>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-sm">
        <SearchSelect
          value={skuFilter}
          onChange={setSkuFilter}
          className="w-72"
          aria-label={t('stock.col.sku')}
          options={[
            { id: '', label: t('order.filter.all') },
            ...skus.map((s) => ({
              id: s.id,
              label: `${s.code} — ${s.nameLt}`,
              imageUrl: s.photoUrl,
              availableQty: s.availableQty,
              maxProducibleQty: s.maxProducibleQty ?? undefined,
            })),
          ]}
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as MovementType | '')}
          className="rounded border border-border bg-background px-2 py-1"
          aria-label={t('stock.col.type')}
        >
          <option value="">{t('order.filter.all')}</option>
          {MOVEMENT_TYPES.map((typ) => (
            <option key={typ} value={typ}>
              {t(`stock.type.${typ}`)}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label={t('order.filter.placed_from')}
          className="rounded border border-border bg-background px-2 py-1"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label={t('order.filter.placed_to')}
          className="rounded border border-border bg-background px-2 py-1"
        />
      </div>

      {rows === null ? null : rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">{t('stock.movements.empty')}</div>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted text-left">
              <tr>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('stock.col.timestamp')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('stock.col.type')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('stock.col.sku')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium text-right">
                  {t('stock.col.qty')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('stock.col.actor')}
                </th>
                <th scope="col" className="px-3 py-2 font-medium">
                  {t('stock.col.reason')}
                </th>
              </tr>
            </thead>
            <tbody>
              {paged.visible.map((m) => {
                const qtyNum = Number(m.qty);
                const note = [m.reason, m.comment].filter(Boolean).join(' · ');
                return (
                  <tr key={m.id} className="border-t border-border">
                    <td className="px-3 py-2 text-xs">{formatDate(m.timestamp)}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${TYPE_CLASS[m.type]}`}
                      >
                        {t(`stock.type.${m.type}`)}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="font-mono text-xs">{m.sku.code}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{m.sku.nameLt}</span>
                    </td>
                    <td
                      className={`px-3 py-2 text-right tabular-nums ${qtyNum < 0 ? 'text-red-600' : ''}`}
                    >
                      {qtyNum > 0 ? '+' : ''}
                      {qtyNum.toLocaleString('lt-LT')}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {m.actor?.displayName.trim() || m.actor?.email || '—'}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {m.sourceOrder ? (
                        <Link
                          to={`/orders/${m.sourceOrder.id}`}
                          className="font-mono text-foreground hover:underline"
                        >
                          {m.sourceOrder.code}
                        </Link>
                      ) : null}
                      {m.sourcePallet ? (
                        <Link
                          to={`/pallets/${m.sourcePallet.id}`}
                          className="font-mono text-foreground hover:underline"
                        >
                          {m.sourcePallet.code}
                        </Link>
                      ) : null}
                      {(m.sourceOrder || m.sourcePallet) && note ? ' · ' : null}
                      {note}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {paged.hasMore || paged.expanded ? (
            <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-muted-foreground">
              <span>
                {t('common.showing_of')
                  .replace('{{shown}}', String(paged.shown))
                  .replace('{{total}}', String(paged.total))}
              </span>
              <span className="flex gap-3">
                {paged.hasMore ? (
                  <button
                    type="button"
                    onClick={paged.showMore}
                    className="underline hover:text-foreground"
                  >
                    {t('common.show_more').replace(
                      '{{n}}',
                      String(Math.min(paged.step, paged.remaining)),
                    )}
                  </button>
                ) : null}
                {paged.expanded ? (
                  <button
                    type="button"
                    onClick={paged.showLess}
                    className="underline hover:text-foreground"
                  >
                    {t('common.show_less')}
                  </button>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
